import * as React from "react";
import { StyleSheet, View } from "react-native";
import { ConnectorType } from "@axio/graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomDropdown from "../../../common/general/CustomDropDown/CustomDropdown";
import {
  CONNECTOR_NO_ACCESS,
  CONNECTOR_ROLE_OPTIONS,
  type ConnectorAccessSelection,
  getConnectorAccessSelection,
  getEnabledRolesForAccessSelection,
} from "../../../../utils/integrations/roleAccess";
import type { CatalogConnector, EnabledConnectorInstance } from "../catalog.utils";
import { ConnectorLogo } from "../ConnectorDetails/ConnectorLogo";

const ACCESS_OPTIONS = [
  { label: "No access", value: CONNECTOR_NO_ACCESS },
  ...CONNECTOR_ROLE_OPTIONS.map((role) => ({ label: role, value: role })),
];

type AccessControlTabProps = {
  catalog: CatalogConnector[];
  enabledConnectors: EnabledConnectorInstance[];
  savingConnectorType: ConnectorType | null;
  errorMessage: string | null;
  onRoleAccessChange: (connectorType: ConnectorType, enabledRoles: string[]) => Promise<void> | void;
};

const getCatalogConnector = (catalog: CatalogConnector[], connectorType: ConnectorType): CatalogConnector | null =>
  catalog.find((connector) => connector.connectorType === connectorType) ?? null;

export function AccessControlTab({
  catalog,
  enabledConnectors,
  savingConnectorType,
  errorMessage,
  onRoleAccessChange,
}: AccessControlTabProps) {
  const persistedSelectionByConnectorType = React.useMemo(
    () =>
      new Map(
        enabledConnectors.map((connector) => [
          connector.connectorType,
          getConnectorAccessSelection(connector.enabledRoles),
        ])
      ),
    [enabledConnectors]
  );
  const [draftSelectionByConnectorType, setDraftSelectionByConnectorType] = React.useState<
    Record<string, ConnectorAccessSelection>
  >({});
  const [openDropdownConnectorType, setOpenDropdownConnectorType] = React.useState<ConnectorType | null>(null);

  React.useEffect(() => {
    setDraftSelectionByConnectorType((current) => {
      let changed = false;
      const next: Record<string, ConnectorAccessSelection> = {};

      Object.entries(current).forEach(([connectorType, draftSelection]) => {
        const persistedSelection = persistedSelectionByConnectorType.get(connectorType as ConnectorType);

        if (!persistedSelection || persistedSelection === draftSelection) {
          changed = true;
          return;
        }

        next[connectorType] = draftSelection;
      });

      return changed ? next : current;
    });
  }, [persistedSelectionByConnectorType]);

  const handleSelectionChange = (connectorType: ConnectorType, selection: ConnectorAccessSelection) => {
    const persistedSelection = persistedSelectionByConnectorType.get(connectorType);

    setOpenDropdownConnectorType(null);

    setDraftSelectionByConnectorType((current) => {
      if (persistedSelection === selection) {
        const { [connectorType]: _removedSelection, ...next } = current;
        return next;
      }

      return {
        ...current,
        [connectorType]: selection,
      };
    });

    if (persistedSelection !== selection) {
      onRoleAccessChange(connectorType, getEnabledRolesForAccessSelection(selection));
    }
  };

  if (enabledConnectors.length === 0) {
    return (
      <View testID="role-access-table" style={styles.container}>
        <View style={styles.emptyState}>
          <CustomText
            text="Enable a connector from the catalog before assigning role access."
            size="s"
            useTranslationText={false}
            textType="text"
            role="text"
            style={styles.mutedText}
          />
        </View>
      </View>
    );
  }

  return (
    <View testID="role-access-table" style={styles.container}>
      <View style={styles.header}>
        <View style={styles.connectorColumn}>
          <CustomText
            text="Connector"
            size="xs"
            useTranslationText={false}
            textType="text"
            weight="bold"
            role="text"
            style={styles.columnLabel}
          />
        </View>
        <View style={styles.accessColumn}>
          <CustomText
            text="Minimum role"
            size="xs"
            useTranslationText={false}
            textType="text"
            weight="bold"
            role="text"
            style={styles.columnLabel}
          />
        </View>
        <View style={styles.actionColumn} />
      </View>

      {enabledConnectors.map((connector, index) => {
        const catalogConnector = getCatalogConnector(catalog, connector.connectorType);
        const connectorName = catalogConnector?.name ?? connector.connectorType;
        const selectedAccess =
          draftSelectionByConnectorType[connector.connectorType] ?? getConnectorAccessSelection(connector.enabledRoles);
        const persistedAccess =
          persistedSelectionByConnectorType.get(connector.connectorType) ??
          getConnectorAccessSelection(connector.enabledRoles);
        const isSaving = savingConnectorType === connector.connectorType;
        const isDropdownOpen = openDropdownConnectorType === connector.connectorType;
        const isLast = index === enabledConnectors.length - 1;

        return (
          <View key={connector.id} style={[styles.row, isLast && styles.rowLast, isDropdownOpen && styles.rowOpen]}>
            <View style={styles.connectorColumn}>
              {catalogConnector ? (
                <View testID={`role-access-icon-${connector.connectorType}`} style={styles.connectorLogo}>
                  <ConnectorLogo iconKey={catalogConnector.icon} category={catalogConnector.category} size={36} />
                </View>
              ) : null}
              <View style={styles.connectorText}>
                <CustomText
                  text={connectorName}
                  size="s"
                  useTranslationText={false}
                  textType="text"
                  weight="bold"
                  role="text"
                  style={styles.connectorName}
                />
                <CustomText
                  text={persistedAccess === CONNECTOR_NO_ACCESS ? "No access" : persistedAccess}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  role="text"
                  style={styles.mutedText}
                />
              </View>
            </View>

            <View style={styles.accessColumn}>
              <CustomDropdown
                key={`${connector.connectorType}:${selectedAccess}`}
                options={ACCESS_OPTIONS}
                value={selectedAccess}
                onChange={(selection) =>
                  handleSelectionChange(connector.connectorType, selection as ConnectorAccessSelection)
                }
                accessibilityLabel={`${connectorName} minimum role`}
                open={isDropdownOpen}
                onOpenChange={(isOpen) => {
                  setOpenDropdownConnectorType(isOpen ? connector.connectorType : null);
                }}
                disabled={isSaving}
                containerStyle={[styles.roleDropdown, isDropdownOpen && styles.roleDropdownOpen]}
                dropdownStyle={styles.roleDropdownField}
                dropdownContainerStyle={styles.roleDropdownMenu}
                hideItemBorders
              />
            </View>

            <View style={styles.actionColumn}>
              {isSaving ? (
                <CustomText
                  text="Saving"
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  role="text"
                  weight="bold"
                  style={styles.savingText}
                />
              ) : null}
            </View>
          </View>
        );
      })}

      {errorMessage ? (
        <CustomText
          text={errorMessage}
          size="s"
          useTranslationText={false}
          textType="text"
          role="alert"
          style={styles.errorText}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
  },
  header: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#D1D5DB",
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
  },
  row: {
    minHeight: 92,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    backgroundColor: "#FFFFFF",
    zIndex: 0,
  },
  rowLast: {
    borderBottomWidth: 0,
    borderBottomLeftRadius: 9,
    borderBottomRightRadius: 9,
  },
  rowOpen: {
    zIndex: 10000,
  },
  connectorColumn: {
    width: 260,
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 16,
  },
  accessColumn: {
    flex: 1,
  },
  actionColumn: {
    width: 92,
    alignItems: "flex-end",
  },
  columnLabel: {
    color: "#667085",
    textTransform: "uppercase",
  },
  connectorLogo: {
    marginRight: 12,
  },
  connectorText: {
    minWidth: 0,
    flex: 1,
  },
  connectorName: {
    color: "#101828",
    marginBottom: 4,
  },
  mutedText: {
    color: "#667085",
  },
  roleDropdown: {
    width: 220,
  },
  roleDropdownOpen: {
    zIndex: 10000,
    elevation: 10000,
  },
  roleDropdownField: {
    minHeight: 40,
    borderColor: "#D0D5DD",
    shadowOpacity: 0,
  },
  roleDropdownMenu: {
    borderColor: "#D0D5DD",
  },
  savingText: {
    color: "#2563EB",
  },
  errorText: {
    color: "#B42318",
    marginTop: 12,
    paddingHorizontal: 20,
  },
  emptyState: {
    minHeight: 180,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
});
